import React, { useState, useEffect } from "react";
import { getAuth, reauthenticateWithCredential, updatePassword, EmailAuthProvider } from "firebase/auth";
import { db } from "../../../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";

export default function CorporateUpdatePassword() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [formStatus, setFormStatus] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  // 🔹 Şirket bilgisini çek (dashboard ile aynı yapı)
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const stored = localStorage.getItem("companyData");
        if (!stored) {
          setFormStatus("Lütfen tekrar giriş yapınız.");
          setLoading(false);
          return;
        }

        const parsed = JSON.parse(stored);
        const email = parsed?.email;
        const taxNumber = parsed?.taxNumber;
        
        if (!email || !taxNumber) {
          setFormStatus("Geçersiz oturum verisi.");
          setLoading(false);
          return;
        }

        // Dashboard ile aynı şekilde şirket verisini çek
        const companiesRef = collection(db, "companies");
        const q = query(
          companiesRef, 
          where("taxInfo.taxNumber", "==", Number(taxNumber))
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setFormStatus("Şirket bilgisi bulunamadı.");
          setLoading(false);
          return;
        }

        const docSnap = snapshot.docs[0];
        const data = docSnap.data();

        // Yetkili kişi bilgilerini çek
        let personData = null;
        try {
          const personRef = doc(db, "companies", docSnap.id, "authorized_person", "personel_info");
          const personSnap = await getDoc(personRef);
          if (personSnap.exists()) {
            personData = personSnap.data();
          }
        } catch (error) {
          console.warn("Yetkili kişi bilgisi bulunamadı:", error);
        }

        const companyData = {
          id: docSnap.id,
          company_id: data.company_id || "",
          company_name: data.company_name || data.company_id || "",
          taxInfo: data.taxInfo || {
            taxNumber: Number(taxNumber),
            companyType: "",
          },
          email: data.email || email || "",
          phone: data.phone || (personData?.person_phone || ""),
        };

        setCompany(companyData);
      } catch (error) {
        console.error("Veri çekme hatası:", error);
        setFormStatus("Veri yüklenirken hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, []);

  // 🔐 Şifre güncelleme işlemi - GÜNCELLENDİ
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setFormStatus("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setFormStatus("Lütfen tüm alanları doldurun.");
      return;
    }

    if (newPassword.length < 6) {
      setFormStatus("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }

    if (newPassword === currentPassword) {
      setFormStatus("Yeni şifre mevcut şifreyle aynı olamaz.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormStatus("Yeni şifreler eşleşmiyor.");
      return;
    }

    try {
      setIsUpdating(true);
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user || !company) {
        setFormStatus("Lütfen giriş yapın.");
        setIsUpdating(false);
        return;
      }

      // Dashboard'daki email formatı ile doğrula
      const userEmail = company.email || `${company.taxInfo?.taxNumber}@company.local`;
      
      // 🔹 Mevcut kimliği yeniden doğrula
      const credential = EmailAuthProvider.credential(userEmail, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // 🔹 Firebase kimlik şifresini güncelle
      await updatePassword(user, newPassword);

      // 🔹 Firestore'da şifre güncellemesi YAPMA - Güvenlik nedeniyle
      // Not: Firebase Auth şifreyi hash'ler, Firestore'da saklamaya gerek yok

      setFormStatus("✅ Şifre başarıyla güncellendi. 3 saniye içinde dashboard'a yönlendirileceksiniz...");
      
      setTimeout(() => {
        navigate("/dashboard_corporate");
      }, 3000);
      
    } catch (error) {
      console.error("Şifre değiştirme hatası:", error);
      if (error.code === "auth/wrong-password") {
        setFormStatus("Mevcut şifre hatalı.");
      } else if (error.code === "auth/too-many-requests") {
        setFormStatus("Çok fazla deneme yaptınız. Lütfen 5 dakika sonra tekrar deneyin.");
      } else if (error.code === "auth/weak-password") {
        setFormStatus("Şifre çok zayıf. Daha güçlü bir şifre seçin.");
      } else if (error.code === "auth/requires-recent-login") {
        setFormStatus("Güvenlik nedeniyle lütfen tekrar giriş yapın.");
        setTimeout(() => {
          getAuth().signOut();
          localStorage.removeItem("companyData");
          localStorage.removeItem("loginTimestamp");
          navigate("/login_corporate");
        }, 2000);
      } else {
        setFormStatus("Bir hata oluştu. Lütfen tekrar deneyin.");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // Loading durumu
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer}>
      <div style={styles.container}>
        {/* Üst Bilgi ve Geri Dön */}
        <div style={styles.header}>
          <div style={styles.headerInfo}>
            <h2 style={styles.heading}>
              {company?.company_name || company?.company_id || "Şirket"} - Şifre Değiştir
            </h2>
            <div style={styles.companyInfo}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Vergi No:</span>
                <span style={styles.infoValue}>{company?.taxInfo?.taxNumber || "—"}</span>
              </div>
              {company?.taxInfo?.companyType && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Şirket Türü:</span>
                  <span style={styles.infoValue}>{company.taxInfo.companyType}</span>
                </div>
              )}
            </div>
          </div>
          <button 
            style={styles.backButton}
            onClick={() => navigate("/dashboard_corporate")}
            disabled={isUpdating}
          >
            Dashboard'a Dön
          </button>
        </div>

        {/* Bilgilendirme Kutusu */}
        <div style={styles.infoBanner}>
          <span style={styles.infoIcon}>🔒</span>
          <span>
            Şifrenizi güvenli bir şekilde değiştirebilirsiniz. Güvenliğiniz için şifrenizi kimseyle paylaşmayın.
          </span>
        </div>

        {/* Şifre Değiştirme Formu */}
        <div style={styles.formCard}>
          <form onSubmit={handlePasswordChange} style={styles.form}>
            {/* Mevcut Şifre */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <span style={styles.required}>*</span> Mevcut Şifre
              </label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword.current ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Mevcut şifrenizi giriniz"
                  style={styles.input}
                  disabled={isUpdating}
                />
                <span
                  onClick={() =>
                    setShowPassword((p) => ({ ...p, current: !p.current }))
                  }
                  style={styles.eyeIcon}
                >
                  {showPassword.current ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>
              </div>
            </div>

            {/* Yeni Şifre */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <span style={styles.required}>*</span> Yeni Şifre
                <span style={styles.passwordHint}> (en az 6 karakter)</span>
              </label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword.new ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Yeni şifrenizi giriniz"
                  style={styles.input}
                  disabled={isUpdating}
                />
                <span
                  onClick={() =>
                    setShowPassword((p) => ({ ...p, new: !p.new }))
                  }
                  style={styles.eyeIcon}
                >
                  {showPassword.new ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>
              </div>
            </div>

            {/* Yeni Şifre (Tekrar) */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <span style={styles.required}>*</span> Yeni Şifre (Tekrar)
              </label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Yeni şifrenizi tekrar giriniz"
                  style={styles.input}
                  disabled={isUpdating}
                />
                <span
                  onClick={() =>
                    setShowPassword((p) => ({ ...p, confirm: !p.confirm }))
                  }
                  style={styles.eyeIcon}
                >
                  {showPassword.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>
              </div>
            </div>

            {/* Durum Mesajı */}
            {formStatus && (
              <div style={{
                ...styles.statusContainer,
                backgroundColor: formStatus.includes("✅") ? "#f0fdf4" : "#fef2f2",
                borderColor: formStatus.includes("✅") ? "#86efac" : "#fecaca",
              }}>
                <p style={{
                  ...styles.status,
                  color: formStatus.includes("✅") ? "#047857" : "#dc2626",
                }}>
                  {formStatus}
                </p>
              </div>
            )}

            {/* Butonlar */}
            <div style={styles.buttonGroup}>
              <button
                type="submit"
                disabled={isUpdating || !currentPassword || !newPassword || !confirmPassword}
                style={{
                  ...styles.submitButton,
                  opacity: (isUpdating || !currentPassword || !newPassword || !confirmPassword) ? 0.6 : 1,
                  cursor: (isUpdating || !currentPassword || !newPassword || !confirmPassword) ? "not-allowed" : "pointer",
                }}
              >
                {isUpdating ? (
                  <>
                    <div style={styles.buttonSpinner}></div>
                    Güncelleniyor...
                  </>
                ) : "Şifreyi Güncelle"}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                  setFormStatus("");
                }}
                style={styles.resetButton}
                disabled={isUpdating}
              >
                Temizle
              </button>
            </div>
          </form>

          {/* Şifre Güvenlik İpuçları */}
          <div style={styles.tipsSection}>
            <h4 style={styles.tipsTitle}>📋 Şifre Güvenlik İpuçları:</h4>
            <ul style={styles.tipsList}>
              <li>En az 6 karakter kullanın</li>
              <li>Büyük ve küçük harf kullanın</li>
              <li>Rakam ve özel karakter ekleyin</li>
              <li>Kişisel bilgilerinizi içermesin</li>
              <li>Farklı hesaplar için farklı şifreler kullanın</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// 🎨 Styles - Dashboard ile aynı tema
const styles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "900px",
    margin: "20px auto",
    padding: "0 16px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #0b63d6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "16px",
  },
  loadingText: {
    fontSize: "14px",
    color: "#666",
  },
  container: {
    padding: "20px",
    backgroundColor: "#f7f9fb",
    borderRadius: "12px",
    boxShadow: "0 8px 30px rgba(8,30,70,0.04)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e5e7eb",
  },
  headerInfo: {
    flex: 1,
  },
  heading: {
    fontSize: "20px",
    color: "#0b2b5a",
    marginBottom: "12px",
    textAlign: "left",
  },
  companyInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: "500",
  },
  infoValue: {
    fontSize: "13px",
    color: "#111827",
    fontWeight: "500",
  },
  backButton: {
    backgroundColor: "#6b7280",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    whiteSpace: "nowrap",
  },
  infoBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "13px",
    backgroundColor: "#eff6ff",
    color: "#1e40af",
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "24px",
    border: "1px solid #dbeafe",
  },
  infoIcon: {
    fontSize: "16px",
    color: "#1e40af",
    flexShrink: 0,
    marginTop: "2px",
  },
  formCard: {
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    padding: "24px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  inputGroup: {
    textAlign: "left",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
  },
  required: {
    color: "#dc2626",
    marginRight: "4px",
  },
  passwordHint: {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "normal",
  },
  input: {
    width: "100%",
    padding: "12px 45px 12px 14px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  passwordWrapper: {
    position: "relative",
  },
  eyeIcon: {
    position: "absolute",
    right: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "4px",
    transition: "background-color 0.2s",
  },
  statusContainer: {
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid",
    marginTop: "8px",
  },
  status: {
    fontSize: "14px",
    textAlign: "left",
    margin: 0,
    fontWeight: "500",
  },
  buttonGroup: {
    display: "flex",
    gap: "12px",
    marginTop: "8px",
  },
  submitButton: {
    flex: 3,
    padding: "12px 0",
    fontSize: "15px",
    fontWeight: "600",
    border: "none",
    borderRadius: "8px",
    color: "#fff",
    backgroundColor: "#0b63d6",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  resetButton: {
    flex: 1,
    padding: "12px 0",
    fontSize: "15px",
    fontWeight: "500",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    color: "#374151",
    backgroundColor: "#f9fafb",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  buttonSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid #ffffff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  tipsSection: {
    marginTop: "28px",
    padding: "16px",
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  tipsTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "10px",
  },
  tipsList: {
    margin: 0,
    paddingLeft: "20px",
    fontSize: "13px",
    color: "#6b7280",
    lineHeight: "1.6",
  },
};

// Spinner animation için inline CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

// Import eksikleri için ekle
